import { useCallback, useEffect, useRef, useMemo } from 'react';

import { useAppSelector } from '@/store/hooks';
import { getPublishedStatus } from '@/store/slices/project/selectors';
import { selectUserChannel } from '@/store/slices/user/selectors';
import getPrefixUrl from '@/utils/get-prefix-url';

import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';

import FaviconUploader from '../../controls/favicon-uploader';
import CopyLinkButton from '../../../project-overview/components/project-header-row/controls/buttons/copy-link-button';

import { ISiteDetailMetadata } from '../../settings.types';
import EditStateTextAreaWrapper from '@ui/edit-state-wrapper/edit-state-text-area-wrapper';

interface SiteDetailProps {
  metadata: ISiteDetailMetadata;
  setMetaData: React.Dispatch<React.SetStateAction<{ name: string; description: string }>>;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setPreviewModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const SiteDetail = ({ metadata, setMetaData, setIsEditing, isEditing, setPreviewModalOpen }: SiteDetailProps) => {
  const publishedStatus = useAppSelector(getPublishedStatus);
  const channel = useAppSelector(selectUserChannel);
  const inputRef = useRef<HTMLInputElement>(null);

  const url = useMemo(() => {
    const baseURL = getPrefixUrl(channel?.url);
    return `${baseURL}/${publishedStatus?.url}`;
  }, [channel, publishedStatus]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMetaData((prev) => ({ ...prev, name: e.target.value }));
    },
    [setMetaData]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMetaData((prev) => ({ ...prev, description: e.target.value }));
    },
    [setMetaData]
  );

  const toggleEditing = () => setIsEditing((prev) => !prev);
  const togglePreviewModal = () => setPreviewModalOpen((prev) => !prev);

  useEffect(() => {
    if (inputRef.current && isEditing) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  return (
    <Container
      className="flex padding-20 gap-12 overflow-hidden"
      borderRadius="12px"
      hoverBackground={!isEditing ? 'var(--gray-800)' : ''}
      background={!isEditing ? 'var(--gray-900)' : 'var(--gray-800)'}
    >
      <div className="flex jc-center" style={{ alignSelf: 'flex-start' }}>
        <FaviconUploader />
      </div>

      <div className="height-100-percent width-100-percent flex-column gap-10">
        <div className="flex ai-center jc-space-between gap-4">
          <InputWithOverlay
            inputHeight="32px"
            inputRef={inputRef}
            wrapperClass="flex-1 border-none"
            inputProps={{
              value: metadata.name,
              className: 'H4 padding-0 radius-4',
              style: {
                border: 'none',
                width: '100%',
                padding: '2px',
              },
              disabled: !isEditing,
              onChange: handleNameChange,
            }}
          />
          <OptionalComponent display={!isEditing}>
            <div className="flex ai-center gap-8">
              <Button
                width="32px"
                height="32px"
                borderRadius="100px"
                backgroundColor="var(--gray-800)"
                className="padding-8"
                onClick={toggleEditing}
                hoverBackgroundColor="var(--gray-700)"
              >
                <Icon name={ICONS.EDIT} size={16} color="var(--gray-200)" />
              </Button>
              <OptionalComponent display={!!publishedStatus?.url}>
                <Button
                  width="32px"
                  height="32px"
                  borderRadius="100px"
                  backgroundColor="var(--gray-800)"
                  hoverBackgroundColor="var(--gray-700)"
                  className="padding-8"
                  onClick={togglePreviewModal}
                >
                  <Icon name={ICONS.EXTERNAL_LINK} size={16} color="var(--gray-200)" />
                </Button>
              </OptionalComponent>
            </div>
          </OptionalComponent>
        </div>
        <div className="flex-column ai-flex-start">
          <OptionalComponent display={isEditing && !!publishedStatus?.url}>
            <Text className="L3" color="var(--gray-500)">
              EVENT URL
            </Text>
          </OptionalComponent>
          <OptionalComponent display={!!publishedStatus?.url}>
            <div className="flex ai-center gap-8">
              <Text className="S3">{url}</Text>
              <CopyLinkButton size={12} iconName={ICONS.COPY_DEFAULT} />
            </div>
          </OptionalComponent>
        </div>
        <div className="flex-column ai-flex-start gap-4">
          <OptionalComponent display={isEditing}>
            <Text className="L3" color="var(--gray-500)">
              META DESCRIPTION
            </Text>
          </OptionalComponent>
          <div className="width-100-percent">
            <EditStateTextAreaWrapper
              maxRowsFocused={3}
              maxRowsBlurred={1}
              textAreaProps={{
                value: metadata.description,
                onChange: handleDescriptionChange,
                disabled: !isEditing,
              }}
            />
          </div>
        </div>
      </div>
    </Container>
  );
};

export default SiteDetail;
